const collator = new Intl.Collator(undefined, {numeric: true, sensitivity: "base"})

/** The value a cell sorts by: its `data-sort` attribute when set (a size in bytes, say), otherwise its text. */
export function sortValue(cell: Element | undefined): string {
  if (!cell) return ""
  return (cell.getAttribute("data-sort") ?? cell.textContent ?? "").trim()
}

/**
 * Orders two sort values, digits compared as numbers so that "2.0.10" follows "2.0.4" and "10 MB" follows
 * "9 MB". An empty value goes last in either direction.
 */
export function compareSortValues(a: string, b: string, direction: 1 | -1): number {
  if (a === "" || b === "") return a === b ? 0 : a === "" ? 1 : -1
  const numeric = /^-?\d+(\.\d+)?$/
  const order = numeric.test(a) && numeric.test(b) ? Number(a) - Number(b) : collator.compare(a, b)
  return order * direction
}

type SortState = {column: number; direction: 1 | -1} | null

/**
 * Makes each header a button that sorts the body by its column: ascending, descending, then the
 * original order. Rows keep their `hidden` state, so a search filter survives sorting.
 */
export function makeSortable(table: HTMLElement) {
  const body = table.querySelector("tbody")!
  const original = Array.from(body.children) as HTMLElement[]
  const headers = Array.from(table.querySelectorAll("thead th"))
  let state: SortState = null

  const apply = () => {
    const rows = state === null
      ? original
      : [...original].sort((x, y) =>
        compareSortValues(sortValue(x.children[state!.column]), sortValue(y.children[state!.column]), state!.direction))
    // Group separators only mean something in the original order.
    table.classList.toggle("resorted", state !== null)
    body.append(...rows)
    headers.forEach((th, i) => {
      th.setAttribute("aria-sort", state?.column === i ? (state.direction === 1 ? "ascending" : "descending") : "none")
    })
  }

  headers.forEach((th, column) => {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "sort"
    button.append(...Array.from(th.childNodes))
    th.replaceChildren(button)
    th.setAttribute("aria-sort", "none")
    button.addEventListener("click", () => {
      state = state?.column !== column ? {column, direction: 1}
        : state.direction === 1 ? {column, direction: -1}
          : null
      apply()
    })
  })
}
